import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import securepassword from "../images/secure-password.svg";
import api from '../../services/api';
import Authorization from '../common/Authorization';
import Notification from '../common/Notification';

const SetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false,
  });

  const [uiState, setUiState] = useState({
    errorMessage: '',
    loading: false,
    notification: null,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  let token = queryParams.get("token");

  if (token && token.includes(' ')) {
    token = token.replace(/ /g, '+');
  }

  const togglePasswordVisibility = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field === 'password' ? 'showPassword' : 'showConfirmPassword']: !prev[field === 'password' ? 'showPassword' : 'showConfirmPassword'],
    }));
  };

  const passwordRequirements = [
    { text: 'At least 8 characters', met: formData.password.length >= 8 },
    { text: 'At least one upper case', met: /[A-Z]/.test(formData.password) },
    { text: 'At least one number', met: /\d/.test(formData.password) },
    { text: 'At least one special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) },
  ];

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setUiState((prev) => ({ ...prev, errorMessage: 'Passwords do not match' }));
      return;
    }

    const isValid = passwordRequirements.every(req => req.met);
    if (!isValid) {
      setUiState((prev) => ({ ...prev, errorMessage: 'Please ensure the password meets all the requirements.' }));
      return;
    }

    try {
      setUiState((prev) => ({ ...prev, loading: true, errorMessage: '' }));
      const response = await api.auth.setpassword(token, formData.password);

setUiState((prev) => ({ ...prev, loading: false }));

      if (response.ok) {
        setFormData({
          password: '',
          confirmPassword: '',
          showPassword: false,
          showConfirmPassword: false,
        });
        navigate("/login");
      } else {
setUiState((prev) => ({ ...prev, errorMessage: response.message || 'Failed to set password' }));
      }
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        loading: false,
        errorMessage: 'Something went wrong. Please try again.',
        notification: { message: "Error during set password: " + error.message, type: "error" },
      }));
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Authorization />
      <div className="w-full md:w-1/2 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-md">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 primary-heading">Set Your Password</h2>

          <div className="bg-blue-100 text-blue-700 rounded-md mb-3 px-3 py-2 flex items-center text-sm">
            <img src={securepassword} alt="secure" className='me-2 w-5 h-5' />
            Your email has been verified successfully!
          </div>

          <p className="mb-4 text-gray-600 text-sm md:text-base">Please enter your new password below</p>

          {uiState.errorMessage && (
            <div className="bg-red-100 text-red-700 rounded-md mb-3 px-3 py-2 text-sm">
              {uiState.errorMessage}
            </div>
          )}

          {/* Password Field */}
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Enter Password
            </label>
            <div className="relative">
              <input
                type={formData.showPassword ? 'text' : 'password'}
                id="password"
                value={formData.password}
                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md custom-form-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('password')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {formData.showPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={formData.showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={formData.confirmPassword}
onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md custom-form-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {formData.showConfirmPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className="btn-blue-color w-full text-white font-medium py-2 px-0 rounded-[10px] focus:outline-none focus:shadow-outline"
            disabled={uiState.loading}
          >
            {uiState.loading ? 'Setting Password...' : 'Set Password & Login'}
          </button>

          <div className="mt-6">
            <h3 className="text-base font-semibold mb-2">Password Requirements</h3>
            <ul>
              {passwordRequirements.map((req, index) => (
                <li key={index} className="flex items-center mb-1 text-sm">
                  <span className={`mr-2 ${req.met ? 'text-green-500' : 'text-red-500'}`}>
                    {req.met ? '✓' : '✗'}
                  </span>
                  <span className="text-gray-600">{req.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Notification */}
      {uiState.notification && (
        <Notification
          message={uiState.notification.message}
          type={uiState.notification.type}
          onClose={() => setUiState((prev) => ({ ...prev, notification: null }))}
        />
      )}
    </div>
  );
};

export default SetPassword;
